import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Music, Music2, Mic2, Users, Calendar, Star, BookOpen, Quote } from 'lucide-react';

const ClassicalConservatory: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Vienna Conservatory";
    
    return (
        <div className="min-h-screen bg-[#1a0505] font-serif text-[#f0e6d2] selection:bg-[#c4a661] selection:text-black">
            {/* Top Bar */}
            <div className="bg-[#0f0303] text-[#8c7853] py-2 px-8 text-center font-sans text-[10px] uppercase tracking-[0.3em] border-b border-[#3d1c1c]">
                Celebrating 150 Years of Excellence
            </div>

            {/* Navbar */}
            <nav className="sticky top-0 z-50 bg-[#1a0505]/95 backdrop-blur border-b border-[#3d1c1c]">
                <div className="max-w-6xl mx-auto px-8 h-24 flex justify-between items-center">
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 border border-[#c4a661] rounded-full flex items-center justify-center">
                            <Music2 size={20} className="text-[#c4a661]" />
                        </div>
                        <span className="font-normal text-xl tracking-wide text-white">{schoolName}</span>
                    </div>

                    <div className="hidden md:flex items-center gap-10 font-sans text-xs font-bold text-[#8c7853] uppercase tracking-widest">
                        <a href="#" className="hover:text-[#c4a661] transition-colors">Performance</a>
                        <a href="#" className="hover:text-[#c4a661] transition-colors">Composition</a>
                        <a href="#" className="hover:text-[#c4a661] transition-colors">Faculty</a>
                        <a href="#" className="hover:text-[#c4a661] transition-colors">Box Office</a>
                    </div>

                    <Button variant="outline" className="font-sans border-[#c4a661] text-[#c4a661] hover:bg-[#c4a661] hover:text-[#1a0505] font-bold uppercase tracking-widest text-[10px] h-9 px-6 rounded-none transition-all">
                        Audition
                    </Button>
                </div>
            </nav>

            {/* Hero Section */}
            <header className="relative py-24 px-8 overflow-hidden flex items-center justify-center min-h-[80vh]">
                {/* Background Glow */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#4a0404] rounded-full blur-[150px] opacity-40 pointer-events-none"></div>

                <div className="relative z-10 max-w-4xl text-center">
                     <div className="inline-block mb-8">
                         <div className="w-px h-16 bg-gradient-to-b from-transparent via-[#c4a661] to-transparent mx-auto mb-4"></div>
                         <span className="font-sans text-xs text-[#c4a661] uppercase tracking-[0.4em]">Virtuosity</span>
                     </div>
                     
                     <h1 className="text-5xl md:text-8xl font-normal text-white mb-12 leading-tight">
                        Where Passion <br/>
                        Becomes <span className="italic text-[#c4a661]">Legacy</span>.
                     </h1>
                     
                     <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left max-w-3xl mx-auto border-t border-[#3d1c1c] pt-12">
                         <div className="space-y-2">
                             <h3 className="text-white text-lg italic">Strings</h3>
                             <p className="font-sans text-xs text-stone-500 leading-relaxed">
                                 Master classes with world-renowned soloists and chamber musicians.
                             </p>
                         </div>
                         <div className="space-y-2">
                             <h3 className="text-white text-lg italic">Piano</h3>
                             <p className="font-sans text-xs text-stone-500 leading-relaxed">
                                 A rigorous curriculum focused on technique and interpretative depth.
                             </p>
                         </div>
                         <div className="space-y-2">
                             <h3 className="text-white text-lg italic">Voice</h3>
                             <p className="font-sans text-xs text-stone-500 leading-relaxed">
                                 Opera and recital training in the bel canto tradition.
                             </p>
                         </div>
                     </div>
                </div>
            </header>

            {/* Upcoming Events / Schedule */}
            <section className="bg-[#120303] py-20 px-8 border-y border-[#3d1c1c]">
                <div className="max-w-5xl mx-auto">
                    <div className="flex justify-between items-end mb-12">
                        <h2 className="text-3xl font-normal text-white">Season Highlights</h2>
                        <a href="#" className="font-sans text-[10px] uppercase tracking-widest text-[#c4a661] flex items-center gap-2 hover:gap-3 transition-all">
                            View Calendar <Calendar size={14} />
                        </a>
                    </div>

                    <div className="grid gap-px bg-[#3d1c1c] border border-[#3d1c1c]">
                        {[
                            { day: "12", month: "OCT", title: "Symphony Orchestra", subtitle: "Mahler: Symphony No. 5" },
                            { day: "24", month: "OCT", title: "Chamber Music Series", subtitle: "The String Quartets of Beethoven" },
                            { day: "05", month: "NOV", title: "Piano Requiem", subtitle: "Guest Artist: Elena Rostova" },
                        ].map((event, i) => (
                            <div key={i} className="bg-[#1a0505] p-6 md:p-8 flex items-center gap-8 hover:bg-[#2b0a0a] transition-colors cursor-pointer group">
                                <div className="font-sans text-center min-w-[60px]">
                                    <span className="block text-2xl font-bold text-white mb-1 group-hover:text-[#c4a661] transition-colors">{event.day}</span>
                                    <span className="block text-[10px] font-bold text-[#5c4d3c] uppercase tracking-widest">{event.month}</span>
                                </div>
                                <div className="w-px h-12 bg-[#3d1c1c]"></div>
                                <div>
                                    <h3 className="text-xl text-white mb-1 font-normal group-hover:translate-x-2 transition-transform">{event.title}</h3>
                                    <p className="font-sans text-xs text-[#8c7853] uppercase tracking-widest">{event.subtitle}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

             {/* Footer */}
            <footer className="bg-[#0f0303] text-[#5c4d3c] py-20 px-8">
                 <div className="max-w-6xl mx-auto text-center">
                     <div className="mb-12">
                         <h2 className="text-2xl font-normal text-white mb-4">{schoolName}</h2>
                         <p className="font-sans text-xs uppercase tracking-[0.2em] max-w-md mx-auto leading-relaxed">
                            "Music expresses that which cannot be said and on which it is impossible to be silent."
                         </p>
                     </div>
                     
                     <div className="flex justify-center gap-8 md:gap-16 font-sans text-[10px] font-bold uppercase tracking-widest text-[#8c7853]">
                         <a href="#" className="hover:text-white transition-colors">Support Us</a>
                         <a href="#" className="hover:text-white transition-colors">Hire Musicians</a>
                         <a href="#" className="hover:text-white transition-colors">Box Office</a>
                     </div>
                     
                     <div className="mt-12 pt-8 border-t border-[#3d1c1c] text-[10px] font-sans">
                         © 2026 {schoolName}. All rights reserved.
                     </div>
                 </div>
            </footer>
        </div>
    );
};

export default ClassicalConservatory;
